// packages/web-ui/src/api/clients/changeset.ts
//
// Story W4-S4 AC #10 — typed API client for the six /api/v1/changeset/*
// endpoints (Arch §3.2.6 lines 267–272). Returns `Result<T, ApiError>`
// per Arch §3.4 + CLAUDE.md "Coding Standards" (no throws across the API
// boundary). The wrappers thread through the existing W2-S1 `apiCall`
// helper so the bearer-token / X-Request-Id / envelope-translation
// behaviour is centralised.
//
// SVG preview is special-cased: `apiCall` is JSON-only, so the wrapper
// drops down to a raw `fetch` for the `image/svg+xml` body.

import type { Result } from '@tessa-lean/base';
import type {
  ChangesetSummary,
  KbChangeset,
  KbEntityState,
} from '@tessa-lean/base';

import { apiCall, type ApiContext, type ApiError } from '../client.js';

export interface ChangesetGetResponse {
  changeset: KbChangeset;
  entityStates: KbEntityState[];
}

export interface ChangesetListResponse {
  changesets: ChangesetSummary[];
}

export interface KbEntityActionResponse {
  queuedAt: string;
}

export interface KbBulkActionResponse {
  queuedAt: string;
  entityIds: string[];
}

/**
 * Response from `POST /api/v1/changeset/:changesetId/undo`. The server queues
 * the undo and returns immediately; the actual revert is delivered via the
 * `kb:changesetUndone` SSE event so the SPA can re-enable its UI on the
 * round-trip.
 */
export interface KbUndoActionResponse {
  queuedAt: string;
}

/** The 409 envelope shape for the dependency-violation case (FR-BACK-20). */
export const DEPENDENCY_VIOLATION_CODE = 'DEPENDENCY_VIOLATION';

/**
 * `GET /api/v1/changeset/:changesetId` → `{ changeset, entityStates }`.
 * `404` → `Result.err({ code: 'NOT_FOUND', ... })`.
 */
export function getChangeset(
  ctx: ApiContext,
  changesetId: string,
): Promise<Result<ChangesetGetResponse, ApiError>> {
  return apiCall<undefined, ChangesetGetResponse>(
    ctx,
    'GET',
    `/api/v1/changeset/${encodeURIComponent(changesetId)}`,
  );
}

/**
 * W4-S7 — `GET /api/v1/changesets` → `{ changesets: ChangesetSummary[] }`.
 * Returns the list of staged changesets (active + history) for the Diff
 * destination ContextPanel. Sorted createdAt-desc by the server.
 */
export function listChangesets(
  ctx: ApiContext,
): Promise<Result<ChangesetListResponse, ApiError>> {
  return apiCall<undefined, ChangesetListResponse>(
    ctx,
    'GET',
    '/api/v1/changesets',
  );
}

/**
 * `POST /api/v1/changeset/:changesetId/entity/:entityId/accept` → `{ queuedAt }`.
 * `409 DEPENDENCY_VIOLATION` when an ancestor is rejected/unresolvable.
 */
export function acceptEntity(
  ctx: ApiContext,
  changesetId: string,
  entityId: string,
): Promise<Result<KbEntityActionResponse, ApiError>> {
  return apiCall<Record<string, never>, KbEntityActionResponse>(
    ctx,
    'POST',
    `/api/v1/changeset/${encodeURIComponent(changesetId)}/entity/${encodeURIComponent(entityId)}/accept`,
    {},
  );
}

/** `POST /api/v1/changeset/:changesetId/entity/:entityId/reject` → `{ queuedAt }`. */
export function rejectEntity(
  ctx: ApiContext,
  changesetId: string,
  entityId: string,
): Promise<Result<KbEntityActionResponse, ApiError>> {
  return apiCall<Record<string, never>, KbEntityActionResponse>(
    ctx,
    'POST',
    `/api/v1/changeset/${encodeURIComponent(changesetId)}/entity/${encodeURIComponent(entityId)}/reject`,
    {},
  );
}

/** `POST /api/v1/changeset/:changesetId/accept-all` → `{ queuedAt, entityIds }`. */
export function acceptAll(
  ctx: ApiContext,
  changesetId: string,
): Promise<Result<KbBulkActionResponse, ApiError>> {
  return apiCall<Record<string, never>, KbBulkActionResponse>(
    ctx,
    'POST',
    `/api/v1/changeset/${encodeURIComponent(changesetId)}/accept-all`,
    {},
  );
}

/** `POST /api/v1/changeset/:changesetId/reject-all` → `{ queuedAt, entityIds }`. */
export function rejectAll(
  ctx: ApiContext,
  changesetId: string,
): Promise<Result<KbBulkActionResponse, ApiError>> {
  return apiCall<Record<string, never>, KbBulkActionResponse>(
    ctx,
    'POST',
    `/api/v1/changeset/${encodeURIComponent(changesetId)}/reject-all`,
    {},
  );
}

/**
 * KB-undo: `POST /api/v1/changeset/:changesetId/undo` → `{ queuedAt }`. The
 * server reverts the most-recently applied KB-changeset (or the specific
 * changeset id if it is still in the multi-level ring buffer); the round-trip
 * outcome lands as a `kb:changesetUndone` SSE event.
 */
export function undoChangeset(
  ctx: ApiContext,
  changesetId: string,
): Promise<Result<KbUndoActionResponse, ApiError>> {
  return apiCall<Record<string, never>, KbUndoActionResponse>(
    ctx,
    'POST',
    `/api/v1/changeset/${encodeURIComponent(changesetId)}/undo`,
    {},
  );
}

/**
 * `GET /api/v1/changeset/:id/entity/:entityId/preview.svg` → raw SVG markup.
 * `204 No Content` when the preview is not yet generated; the wrapper
 * surfaces this as `Result.err({ code: 'PREVIEW_PENDING', ... })` so the
 * UI can render the FR-DIFF-4 text-only fallback consistently.
 *
 * TODO(W4-S4 follow-up): consume the response `ETag` for conditional
 * fetch on subsequent disclosures.
 */
export async function getPreviewSvg(
  ctx: ApiContext,
  changesetId: string,
  entityId: string,
): Promise<Result<string, ApiError>> {
  const requestId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const headers: Record<string, string> = {
    Accept: 'image/svg+xml',
    'X-Request-Id': requestId,
  };
  if (ctx.token !== null) headers.Authorization = `Bearer ${ctx.token}`;

  const fetchImpl = ctx.fetchImpl ?? fetch;
  const path = `/api/v1/changeset/${encodeURIComponent(changesetId)}/entity/${encodeURIComponent(entityId)}/preview.svg`;
  let response: Response;
  try {
    response = await fetchImpl(`${ctx.baseUrl}${path}`, { method: 'GET', headers });
  } catch (err) {
    return {
      ok: false,
      error: {
        code: 'NETWORK_ERROR',
        message: err instanceof Error ? err.message : 'fetch failed',
        retryable: true,
        requestId,
      },
    };
  }
  if (response.status === 204) {
    return {
      ok: false,
      error: {
        code: 'PREVIEW_PENDING',
        message: 'Preview has not been generated yet.',
        retryable: true,
        requestId,
      },
    };
  }
  if (!response.ok) {
    let envelope: Partial<ApiError> = {};
    try {
      envelope = (await response.json()) as Partial<ApiError>;
    } catch {
      // body was not JSON — fall through to defaults
    }
    return {
      ok: false,
      error: {
        code: typeof envelope.code === 'string' ? envelope.code : 'HTTP_ERROR',
        message:
          typeof envelope.message === 'string'
            ? envelope.message
            : `${response.status} ${response.statusText}`,
        retryable: typeof envelope.retryable === 'boolean' ? envelope.retryable : response.status >= 500,
        requestId,
      },
    };
  }
  const text = await response.text();
  return { ok: true, value: text };
}

/** Convenience barrel for tests + slice consumers. */
export const changesetApi = {
  getChangeset,
  listChangesets,
  acceptEntity,
  rejectEntity,
  acceptAll,
  rejectAll,
  getPreviewSvg,
  undoChangeset,
};

export type ChangesetApi = typeof changesetApi;
